import { TaskDelegationPanel } from "@/pages/agent/delegation/task-delegation-panel";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import {
  MDSCard,
  MDSCardVariant,
  MDSIconKind,
  MDSSkeleton,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { useAgentOverlayAnalytics } from "@/pages/agent/analytics/use-agent-overlay-analytics";
import {
  AgentBreadcrumbs,
  type AgentBreadcrumbItem,
} from "@/pages/agent/components/agent-breadcrumbs";
import { AgentHorizontalCardCarousel } from "@/pages/agent/components/agent-horizontal-card-carousel";
import { AgentPageLayout } from "@/pages/agent/components/agent-page-layout";
import { AgentRelationshipTile } from "@/pages/agent/components/agent-relationship-card";
import { AgentSection, AgentSectionEmptyState } from "@/pages/agent/components/agent-section";
import { AgentSourceNoteCard } from "@/pages/agent/components/agent-source-note-card";
import { AgentSurfaceNavigationActiveTab } from "@/pages/agent/components/agent-surface-navigation";
import { AgentTaskDetailCard } from "@/pages/agent/task/agent-task-detail-card";
import { AgentTaskEditor, type AgentTaskEditorValue } from "@/pages/agent/task/agent-task-editor";
import { AgentTaskMetaCard } from "@/pages/agent/task/agent-task-meta-card";
import { buildTaskDetailViewModel } from "@/pages/agent/task/agent-task-page.utils";
import { resolveTaskSourceNotes } from "@/pages/agent/utils/agent-task-source.utils";
import { useAgentIntentGraph } from "@/pages/agent/intent-graph/use-agent-intent-graph";
import { logger } from "@/modules/logger";
import { useAppStore } from "@/store";
import type { AssistantTaskObservable } from "@/store/assistant";
import {
  isClosedAssistantTaskStatus,
  normalizeAssistantTaskStatus,
} from "@/store/assistant/assistant-task-status";
import { AssistantTaskStatus } from "@/store/assistant/types";

interface AgentTaskPageProps {
  taskId: string;
}

export const AgentTaskPage = observer(({ taskId }: AgentTaskPageProps) => {
  const { store } = useAppStore();
  const task = store.assistantTasks.getCurrentAssistantModelById(taskId);
  const isMissing = store.assistantTasks.currentAssistantModelDoesNotExist(taskId);
  const project = task?.projectId
    ? store.assistantProjects.currentAssistantModels.find(item => item.id === task.projectId)
    : undefined;
  const breadcrumbItems: AgentBreadcrumbItem[] = [];

  if (project) {
    breadcrumbItems.push({ label: "Projects", onClick: () => store.agentOverlay.openProjects() });
    breadcrumbItems.push({
      label: project.title || "Untitled project",
      onClick: () => store.agentOverlay.openProject({ projectId: project.id }),
    });
    breadcrumbItems.push({
      label: "Tasks",
      onClick: () => store.agentOverlay.openProjectTasks({ projectId: project.id }),
    });
  } else {
    breadcrumbItems.push({ label: "Tasks", onClick: () => store.agentOverlay.openTasks() });
  }

  breadcrumbItems.push({ label: task?.title || "Task" });

  return (
    <AgentPageLayout activeTab={AgentSurfaceNavigationActiveTab.Tasks}>
      <AgentBreadcrumbs items={breadcrumbItems} />

      {task ? (
        <TaskDetail task={task} />
      ) : isMissing ? (
        <AgentTaskUnavailableState
          description="This task may have been removed or the link may be incorrect."
          title="Task not found"
        />
      ) : (
        <AgentTaskLoadingState />
      )}
    </AgentPageLayout>
  );
});

const TaskDetail = observer(({ task }: { task: AssistantTaskObservable }) => {
  const { store } = useAppStore();
  const analytics = useAgentOverlayAnalytics();
  const graph = useAgentIntentGraph();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isSavingTask, setIsSavingTask] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const taskNoteReferences = store.assistantTaskNoteReferences.currentAssistantModels.filter(
    reference => reference.modelData.task_id === task.id
  );
  const sourceNotes = resolveTaskSourceNotes({
    references: taskNoteReferences,
    getNoteById: store.notes.getNoteObservableById,
  });
  const taskDetail = buildTaskDetailViewModel({
    task,
    projects: store.assistantProjects.currentAssistantModels,
    projectsLoaded: store.assistantProjects.currentAssistantModelsLoaded,
    followUps: store.assistantFollowUps.currentAssistantModels,
    followUpsLoaded: store.assistantFollowUps.currentAssistantModelsLoaded,
    sourceNotes,
    sourcesLoaded: store.assistantTaskNoteReferences.currentAssistantModelsLoaded,
  });
  const intent = graph.data?.tasks?.find(item => item.id === task.id);
  const project = task.projectId
    ? store.assistantProjects.currentAssistantModels.find(item => item.id === task.projectId)
    : undefined;
  const ancestorProjectIds = resolveAncestorProjectIds({
    projectId: task.projectId,
    hierarchyLinks: graph.data?.project_hierarchy_links ?? [],
  });
  const ancestorProjects = ancestorProjectIds
    .map(projectId =>
      store.assistantProjects.currentAssistantModels.find(item => item.id === projectId)
    )
    .filter(item => item !== undefined);
  const projectLineageIds = new Set([task.projectId, ...ancestorProjectIds].filter(Boolean));
  const goalIds = new Set(
    (graph.data?.project_goal_links ?? [])
      .filter(link => projectLineageIds.has(link.project_id))
      .map(link => link.goal_id)
  );
  const goals = (graph.data?.goals ?? []).filter(item => goalIds.has(item.id));
  const parentTask = intent?.parent_task_id
    ? store.assistantTasks.getCurrentAssistantModelById(intent.parent_task_id)
    : undefined;
  const parentTaskId = intent?.parent_task_id;
  const childTasks = (intent?.child_task_ids ?? [])
    .map(taskId => store.assistantTasks.getCurrentAssistantModelById(taskId))
    .filter(item => item !== undefined);
  const informationItems = (intent?.information_item_ids ?? [])
    .map(informationItemId =>
      graph.data?.information_items?.find(item => item.id === informationItemId)
    )
    .filter(item => item !== undefined);
  const hasRelatedItems =
    project !== undefined ||
    ancestorProjects.length > 0 ||
    goals.length > 0 ||
    (parentTaskId !== null && parentTaskId !== undefined) ||
    childTasks.length > 0 ||
    informationItems.length > 0;
  const openProject = ({ projectId }: { projectId: string }) => {
    analytics.trackItemClick({
      itemId: projectId,
      itemKind: "project",
      source: "task_project",
    });
    store.agentOverlay.openProject({ projectId });
  };

  const openSourceNote = ({ noteId }: { noteId: string }) => {
    analytics.trackItemClick({
      itemId: noteId,
      itemKind: "note",
      source: "task_source",
    });
    store.agentOverlay.openSourceNote({ noteId });
  };
  const toggleTaskComplete = async () => {
    if (isUpdatingStatus) return;

    const currentStatus = normalizeAssistantTaskStatus({ status: task.status });
    const nextStatus = isClosedAssistantTaskStatus({ status: currentStatus })
      ? AssistantTaskStatus.Pending
      : AssistantTaskStatus.Completed;
    let action: "completed" | "marked_incomplete" | "reopened" = "completed";

    if (nextStatus !== AssistantTaskStatus.Completed) {
      action = currentStatus === AssistantTaskStatus.Completed ? "marked_incomplete" : "reopened";
    }

    setStatusError(null);
    setIsUpdatingStatus(true);
    analytics.trackTaskStatusToggle({ action, taskId: task.id });

    try {
      await task.updateStatus({ status: nextStatus });
    } catch {
      logger.error({
        message: "[AgentTaskPage] Failed to update task status",
        info: { taskId: task.id },
      });
      setStatusError("Task status could not be updated. Please try again.");
    } finally {
      setIsUpdatingStatus(false);
    }
  };
  const saveTask = async (value: AgentTaskEditorValue) => {
    if (isSavingTask) return;

    setEditError(null);
    setIsSavingTask(true);

    try {
      await task.update(value);
      setIsEditorOpen(false);
    } catch {
      logger.error({
        message: "[AgentTaskPage] Failed to edit task",
        info: { taskId: task.id },
      });
      setEditError("Task changes could not be saved. Please try again.");
    } finally {
      setIsSavingTask(false);
    }
  };

  return (
    <StyledPageStack>
      <AgentTaskDetailCard
        taskDetail={taskDetail}
        onOpenProject={openProject}
        onOpenActivities={({ taskId }) => {
          store.agentOverlay.openActivities({ kind: "task", taskId });
        }}
        onEdit={() => {
          setEditError(null);
          setIsEditorOpen(true);
        }}
        onToggleComplete={() => void toggleTaskComplete()}
        isUpdatingStatus={isUpdatingStatus}
        mutationError={statusError}
      />

      <TaskDelegationPanel
        key={task.id}
        taskId={task.id}
        pending={task.status === "pending"}
        onComplete={() => void toggleTaskComplete()}
      />

      {taskDetail.sourcesLoading || taskDetail.sourceNotes.length > 0 ? (
        <AgentSection
          title="Source notes"
          trailing={
            !taskDetail.sourcesLoading ? (
              <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
                {taskDetail.sourceNotes.length}{" "}
                {taskDetail.sourceNotes.length === 1 ? "note" : "notes"}
              </MDSText>
            ) : null
          }
        >
          {taskDetail.sourcesLoading ? (
            <AgentSectionEmptyState>Loading source notes…</AgentSectionEmptyState>
          ) : (
            <AgentHorizontalCardCarousel ariaLabel="Source notes">
              {taskDetail.sourceNotes.map(note => (
                <AgentSourceNoteCard key={note.id} note={note} onOpen={openSourceNote} />
              ))}
            </AgentHorizontalCardCarousel>
          )}
        </AgentSection>
      ) : null}

      {hasRelatedItems ? (
        <AgentSection
          description="Browse the work and outcomes that give this task context."
          title="Related"
        >
          <AgentHorizontalCardCarousel ariaLabel="Related">
            {project ? (
              <AgentRelationshipTile
                description={project.description}
                icon={MDSIconKind.Folder}
                label="Project"
                title={project.title || "Untitled project"}
                onOpen={() => store.agentOverlay.openProject({ projectId: project.id })}
              />
            ) : null}
            {ancestorProjects.map(item => (
              <AgentRelationshipTile
                key={item.id}
                description={item.description}
                icon={MDSIconKind.Folder}
                label="Parent project"
                title={item.title || "Untitled project"}
                onOpen={() => store.agentOverlay.openProject({ projectId: item.id })}
              />
            ))}
            {goals.map(goal => (
              <AgentRelationshipTile
                key={goal.id}
                description={goal.description}
                icon={MDSIconKind.Compass}
                label="Goal"
                title={goal.title || "Untitled goal"}
                onOpen={() => store.agentOverlay.openGoal({ goalId: goal.id })}
              />
            ))}
            {parentTaskId ? (
              <AgentRelationshipTile
                description={parentTask?.description}
                icon={MDSIconKind.ListCheck}
                label="Parent task"
                title={parentTask?.title || "Untitled task"}
                onOpen={() => store.agentOverlay.openTask({ taskId: parentTaskId })}
              />
            ) : null}
            {childTasks.map(childTask => (
              <AgentRelationshipTile
                key={childTask.id}
                description={childTask.description}
                icon={MDSIconKind.ListCheck}
                label="Subtask"
                title={childTask.title || "Untitled task"}
                onOpen={() => store.agentOverlay.openTask({ taskId: childTask.id })}
              />
            ))}
            {informationItems.map(item => (
              <AgentRelationshipTile
                key={item.id}
                description={item.kind}
                icon={MDSIconKind.Document}
                label="Information"
                title={item.descriptor || "Information"}
                onOpen={() =>
                  store.agentOverlay.openInformationItem({ informationItemId: item.id })
                }
              />
            ))}
          </AgentHorizontalCardCarousel>
        </AgentSection>
      ) : null}

      <AgentSection title="Details">
        <AgentTaskMetaCard intent={intent} taskDetail={taskDetail} />
      </AgentSection>
      <AgentTaskEditor
        error={editError}
        isOpen={isEditorOpen}
        isSaving={isSavingTask}
        projects={store.assistantProjects.currentAssistantModels}
        task={task}
        onClose={() => setIsEditorOpen(false)}
        onSave={value => void saveTask(value)}
      />
    </StyledPageStack>
  );
});

const resolveAncestorProjectIds = ({
  hierarchyLinks,
  projectId,
}: {
  hierarchyLinks: Array<{ parent_project_id: string; project_id: string }>;
  projectId: string | null | undefined;
}) => {
  if (!projectId) return [];

  const ancestorProjectIds: string[] = [];
  const visitedProjectIds = new Set([projectId]);
  let currentProjectIds = [projectId];

  while (currentProjectIds.length > 0) {
    const parentProjectIds = hierarchyLinks
      .filter(link => currentProjectIds.includes(link.project_id))
      .map(link => link.parent_project_id)
      .filter(parentProjectId => !visitedProjectIds.has(parentProjectId));

    for (const parentProjectId of parentProjectIds) {
      visitedProjectIds.add(parentProjectId);
      ancestorProjectIds.push(parentProjectId);
    }

    currentProjectIds = parentProjectIds;
  }

  return ancestorProjectIds;
};

const StyledPageStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

export const AgentTaskUnavailableState = ({
  description,
  title,
}: {
  description: string;
  title: string;
}) => (
  <StyledStateCard variant={MDSCardVariant.Chrome}>
    <MDSText
      color={MDSTextColor.Primary}
      variant={MDSTextVariant.HeaderS}
      weight={MDSTextWeight.SemiBold}
    >
      {title}
    </MDSText>
    <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphS}>
      {description}
    </MDSText>
  </StyledStateCard>
);

export const AgentTaskLoadingState = () => (
  <StyledStateCard aria-label="Loading task" variant={MDSCardVariant.Chrome}>
    <MDSSkeleton height={24} width="45%" />
    <MDSSkeleton height={16} width="80%" />
    <MDSSkeleton height={16} width="65%" />
  </StyledStateCard>
);

const StyledStateCard = styled(MDSCard)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;
